import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import MainLayout from './components/layout/MainLayout';
import { buildDialogThemeOverrides } from './shared/theme/dialogOverrides';
import { buildMobileDrawerThemeOverrides } from './shared/theme/mobileDrawerOverrides';
import { buildMobileThemeOverrides } from './shared/theme/mobileTheme';
import { buildMobileShellDarkTheme } from './shared/theme/mobileShellDarkTheme';
import { isNativeAppShell } from './utils/mobileView';
import {
  detectThemeSync,
  ensureDefaultThemePreference,
  getStoredThemePreference,
  watchThemeChanges,
} from './utils/themeUtils';
import { MUI_THEME_Z_INDEX, THEME_COLORS } from './shared/constants/theme';
import {
  isNexaStaySlugDomainHost,
  isNexaStayPublicPathname,
  shouldRenderConsoleNexaStayFallback,
  resolveNexaStayPublicLoginRedirect,
} from './nexastay/useNexaStayBasePath';

// Page components – single bundle (no code-splitting)
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import FreeTrialPage from './pages/FreeTrialPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import BookingPage from './pages/BookingPage';
import BookingDashboardPage from './pages/BookingDashboardPage';
import BookingListPage from './pages/BookingListPage';
import GroupBookingDetailPage from './pages/GroupBookingDetailPage';
import WebCheckInListPage from './pages/WebCheckInListPage';
import BookingDetailPage from './pages/BookingDetailPage';
import EditBookingPage from './pages/EditBookingPage';
import ReservationPage from './pages/ReservationPage';
import WalkInForm from './components/WalkInForm';
import CheckInPage from './pages/CheckInPage';
import StayPage from './pages/StayPage';
import CheckOutPage from './pages/CheckOutPage';
import NoShowPage from './pages/NoShowPage';
import RoomAllocationPage from './pages/RoomAllocationPage';
import InventoryDashboardPage from './pages/InventoryDashboardPage';
import StockOperationsDashboardPage from './pages/stock/StockOperationsDashboardPage';
import InventoryListPage from './pages/InventoryListPage';
import ManageInventoryPage from './pages/ManageInventoryPage';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import PaymentListPage from './pages/PaymentListPage';
import OtaCommissionPage from './pages/OtaCommissionPage';
import CashCounterPage from './pages/CashCounterPage';
import DraftInvoicePage from './pages/DraftInvoicePage';
import ReportsPage from './pages/ReportsPage';
import ManageRatePage from './pages/ManageRatePage';
import HousekeepingPage from './pages/HousekeepingPage';
import CustomerHubPage from './pages/CustomerHubPage';
import CustomerListPage from './pages/CustomerListPage';
import CustomerRetentionPage from './pages/CustomerRetentionPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AddCustomerPage from './pages/AddCustomerPage';
import ConfigurationPage from './pages/ConfigurationPage';
import PerformanceDashboardPage from './pages/PerformanceDashboardPage';
import UsersPage from './pages/UsersPage';
import DynamicPricingPage from './pages/DynamicPricingPage';
import DistributionPage from './pages/DistributionPage';
import ProfilePage from './pages/ProfilePage';
import RestaurantPage from './pages/RestaurantPage';
import HallPage from './pages/HallPage';
import HallDetailPage from './pages/HallDetailPage';
import HallEventDetailPage from './pages/HallEventDetailPage';
import HallVendorDetailPage from './pages/HallVendorDetailPage';
import PosRestaurantPage from './pages/PosRestaurantPage';
import PosOpenOrderingPublicPage from './pages/PosOpenOrderingPage';
import PosOpenOrderingCheckoutPublicPage from './pages/PosOpenOrderingCheckoutPage';
import PublicWebCheckInPage from './pages/PublicWebCheckInPage';

import NexaStayLayout from './nexastay/NexaStayLayout';
import HotelLandingPage from './nexastay/HotelLandingPage';
import CheckoutPage from './nexastay/CheckoutPage';
import AboutPage from './nexastay/AboutPage';
import ContactPage from './nexastay/ContactPage';
import NexaStayProfilePage from './nexastay/ProfilePage';
import NexaStayBookingsPage from './nexastay/BookingsPage';
import TermsPage from './nexastay/TermsPage';
import SupportChatFab from './components/support/SupportChatFab';
import StartupSequence from './components/startup/StartupSequence';
import {
  getFirstAllowedModulePath,
  getUserPermissionCodes,
  hasFeaturePermission,
} from './utils/featurePermissions';
import { PermissionProvider } from './auth/permissionProvider';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Staff /login/ must not appear on NexaStay public hosts — send guests back to booking pages. */
function NexaStayPublicLoginRedirect() {
  const [searchParams] = useSearchParams();
  const target = resolveNexaStayPublicLoginRedirect(searchParams.get('next'));
  return <Navigate to={target} replace />;
}

function NexaStayHotelChildRoutes() {
  return (
    <>
      <Route index element={<HotelLandingPage />} />
      <Route path="about" element={<AboutPage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="profile" element={<NexaStayProfilePage />} />
      <Route path="bookings" element={<NexaStayBookingsPage />} />
      <Route path="terms" element={<TermsPage />} />
      <Route path="checkout" element={<CheckoutPage />} />
    </>
  );
}

function RootRoute() {
  const djangoContext = window.DJANGO_CONTEXT || {};
  const user = djangoContext.user || {};

  if (user && user.id) {
    return <Navigate to="/dashboard/" replace />;
  }

  return <Navigate to="/login/" replace />;
}

function StaffApplicationRoutes({ isSlugNexaStayHost }) {
  return (
    <>
      {!isSlugNexaStayHost && <Route path="/" element={<RootRoute />} />}

      {isSlugNexaStayHost ? (
        <Route path="/login/" element={<NexaStayPublicLoginRedirect />} />
      ) : (
        <Route path="/login/" element={<LoginPage />} />
      )}
      <Route path="/pos-restaurant/open/" element={<PosOpenOrderingPublicPage />} />
      <Route path="/pos-restaurant/open/checkout/" element={<PosOpenOrderingCheckoutPublicPage />} />
      <Route path="/web-checkin/:token/" element={<PublicWebCheckInPage />} />
      <Route path="/free-trial/" element={<FreeTrialPage />} />
      <Route path="/password-reset/" element={<ForgotPasswordPage />} />
      <Route path="/password-reset/:uidb64/:token/" element={<ResetPasswordPage />} />
      <Route path="/onboarding/landing/" element={<LandingPage />} />
      <Route path="/nexastay/hotel" element={<Navigate to="/nexastay/hotel/2" replace />} />
      <Route path="/nexastay/hotel/:hotelId" element={<NexaStayLayout />}>
        {NexaStayHotelChildRoutes()}
      </Route>

      {!isSlugNexaStayHost && (
        <Route element={<PermissionProvider><MainLayout /></PermissionProvider>}>
          <Route path="dashboard/" element={withFeaturePermission(['common.view_organization_dashboard', 'common.manage_booking_operations'], <DashboardPage />)} />
          <Route path="dashboard/performance/" element={withFeaturePermission(['common.view_organization_dashboard', 'common.view_reports'], <PerformanceDashboardPage />)} />
          <Route path="dashboard/org/" element={<Navigate to="/dashboard/" replace />} />
          <Route path="restaurant/" element={withFeaturePermission('common.manage_pos_restaurant', <RestaurantPage />)} />
          <Route path="hall/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/dashboard/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/booking-calendar/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/list-events/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/hall-management/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/vendor-management/" element={withFeaturePermission('common.manage_banquet_conference', <HallPage />)} />
          <Route path="hall/vendor/:vendorId/" element={withFeaturePermission('common.manage_banquet_conference', <HallVendorDetailPage />)} />
          <Route path="hall/detail/:hallId/" element={withFeaturePermission('common.manage_banquet_conference', <HallDetailPage />)} />
          <Route path="hall/event/:bookingId/" element={withFeaturePermission('common.manage_banquet_conference', <HallEventDetailPage />)} />
          <Route path="pos-restaurant/" element={withFeaturePermission('common.manage_pos_restaurant', <PosRestaurantPage />)} />
          <Route path="distribution/" element={withFeaturePermission(['common.manage_distribution', 'common.manage_booking_engine'], <DistributionPage />)} />
          <Route path="distribution/channel-setup/" element={withFeaturePermission('common.manage_distribution', <DistributionPage />)} />
          <Route path="distribution/channel-inventory/" element={withFeaturePermission('common.manage_distribution', <DistributionPage />)} />
          <Route path="distribution/channel-mapping/" element={withFeaturePermission('common.manage_distribution', <DistributionPage />)} />
          <Route path="distribution/pms-inventory/" element={withFeaturePermission(['common.manage_distribution', 'common.manage_inventory', 'common.manage_booking_operations'], <DistributionPage />)} />
          <Route path="distribution/inventory-block/" element={withFeaturePermission(['common.manage_distribution', 'common.manage_inventory', 'common.manage_booking_operations'], <DistributionPage />)} />
          <Route path="distribution/nexastay/" element={withFeaturePermission(['common.manage_distribution', 'common.manage_booking_engine'], <DistributionPage />)} />
          <Route path="dynamic-pricing/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/recommendations/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/rules/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/events/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/competitor/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/competitor/rates/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/competitor/map/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/simulate/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/forecast/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/config/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/history/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="dynamic-pricing/history/recent/" element={withFeaturePermission('common.manage_dynamic_pricing', <DynamicPricingPage />)} />
          <Route path="booking/" element={withFeaturePermission('common.manage_booking_operations', <BookingPage />)} />
          <Route path="booking/dashboard/" element={withFeaturePermission(['common.view_organization_dashboard', 'common.view_reports'], <BookingDashboardPage />)} />
          <Route path="booking/list/" element={withFeaturePermission('common.manage_booking_operations', <BookingListPage />)} />
          <Route path="booking/group/" element={<Navigate to="/booking/list/" replace />} />
          <Route path="booking/group/detail/:groupBookingId/" element={withFeaturePermission('common.manage_booking_operations', <GroupBookingDetailPage />)} />
          <Route path="booking/web-checkin/" element={withFeaturePermission('common.manage_web_checkin', <WebCheckInListPage />)} />
          <Route path="booking/detail/:bookingId/" element={withFeaturePermission('common.manage_booking_operations', <BookingDetailPage />)} />
          <Route path="booking/edit/:bookingId/" element={withFeaturePermission('common.modify_booking', <EditBookingPage />)} />
          <Route path="booking/reservation/" element={withFeaturePermission('common.manage_booking_operations', <ReservationPage />)} />
          <Route path="booking/walk-in/" element={withFeaturePermission('common.manage_booking_operations', <WalkInForm />)} />
          <Route path="booking/stay/" element={withFeaturePermission('common.manage_booking_operations', <StayPage />)} />
          <Route path="booking/checkin/" element={withFeaturePermission('common.manage_booking_operations', <CheckInPage />)} />
          <Route path="booking/checkout/" element={withFeaturePermission('common.manage_booking_operations', <CheckOutPage />)} />
          <Route path="booking/no-show/" element={withFeaturePermission('common.manage_booking_operations', <NoShowPage />)} />
          <Route path="booking/room-allocation/" element={withFeaturePermission('common.manage_booking_operations', <RoomAllocationPage />)} />
          <Route path="inventory/" element={withFeaturePermission('common.manage_inventory', <InventoryDashboardPage />)} />
          <Route path="stock/*" element={withFeaturePermission('common.manage_stock', <StockOperationsDashboardPage />)} />
          <Route path="inventory/list/" element={withFeaturePermission('common.manage_inventory', <InventoryListPage />)} />
          <Route path="inventory/manage/" element={withFeaturePermission('common.manage_inventory', <ManageInventoryPage />)} />
          <Route path="finance/" element={withFeaturePermission('common.manage_finance', <FinanceDashboardPage />)} />
          <Route path="finance/payments/" element={withFeaturePermission('common.manage_finance', <PaymentListPage />)} />
          <Route path="finance/ota-commission/" element={withFeaturePermission('common.manage_finance', <OtaCommissionPage />)} />
          <Route path="finance/cash-counter/" element={withFeaturePermission('common.manage_finance', <CashCounterPage />)} />
          <Route path="finance/list/" element={withFeaturePermission('common.manage_finance', <PaymentListPage />)} />
          <Route path="finance/draft-invoice/" element={withFeaturePermission('common.manage_finance', <DraftInvoicePage />)} />
          <Route path="finance/reports/" element={withFeaturePermission('common.manage_finance', <ReportsPage />)} />
          <Route path="finance/rates/" element={withFeaturePermission('common.manage_finance', <ManageRatePage />)} />
          <Route path="finance/setup/" element={<Navigate to="/configuration/finance-setup/" replace />} />
          <Route path="housekeeping/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/room-status/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/cleanings/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/staff/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/maintenance/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/lost-and-found/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/config/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="housekeeping/checklist/" element={withFeaturePermission('common.manage_housekeeping', <HousekeepingPage />)} />
          <Route path="customerhub/" element={withFeaturePermission('common.manage_customer_management', <CustomerHubPage />)} />
          <Route path="customerhub/customers/" element={withFeaturePermission('common.manage_customer_management', <CustomerListPage />)} />
          <Route path="customerhub/retention/" element={withFeaturePermission('common.manage_customer_management', <CustomerRetentionPage />)} />
          <Route path="customerhub/customers/new/" element={withFeaturePermission('common.manage_customer_management', <AddCustomerPage />)} />
          <Route path="customerhub/list/" element={withFeaturePermission('common.manage_customer_management', <CustomerListPage />)} />
          <Route path="customerhub/customers/:guestId/" element={withFeaturePermission('common.manage_customer_management', <CustomerDetailPage />)} />
          <Route path="configuration/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/basic-details/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/rate-plan/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/room-types/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/floors/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/rooms/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/images/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/amenities/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/terms-and-policy/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/finance-setup/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/invoice-setup/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/data-migration/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/config/" element={withFeaturePermission('common.manage_configuration', <ConfigurationPage />)} />
          <Route path="configuration/communications/" element={withFeaturePermission('common.manage_configuration', <Navigate to="/configuration/config/" replace />)} />
          <Route path="users/" element={withFeaturePermission('common.manage_users', <UsersPage />)} />
          <Route path="profile/" element={<ProfilePage />} />
          <Route path="*" element={<Navigate to="/dashboard/" replace />} />
        </Route>
      )}
    </>
  );
}

function AppRouteTree({ isSlugNexaStayHost }) {
  const location = useLocation();

  if (isSlugNexaStayHost) {
    return (
      <Routes>
        <Route path="/" element={<NexaStayLayout />}>
          {NexaStayHotelChildRoutes()}
        </Route>
        {StaffApplicationRoutes({ isSlugNexaStayHost: true })}
      </Routes>
    );
  }

  if (shouldRenderConsoleNexaStayFallback(location.pathname)) {
    return (
      <Routes>
        <Route path="/:hotelId" element={<NexaStayLayout />}>
          {NexaStayHotelChildRoutes()}
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      {StaffApplicationRoutes({ isSlugNexaStayHost: false })}
    </Routes>
  );
}

const BRAND_ACCENT = THEME_COLORS.PRIMARY;

// Create Material-UI theme matching existing design with dark theme support
const buildBaseThemeOptions = (mode = 'light') => ({
  // Keep Select/Menu/Popover/DatePicker above drawers (drawer = 1700 on mobile).
  zIndex: MUI_THEME_Z_INDEX,
  palette: {
    mode: mode,
    primary: {
      main: BRAND_ACCENT,
      dark: THEME_COLORS.PRIMARY_HOVER,
      light: '#5eead4',
      contrastText: '#000000',
    },
    secondary: {
      main: '#424242',
    },
    ...(mode === 'dark' ? {
      background: {
        default: THEME_COLORS.BACKGROUND_DARK,
        paper: '#2a2a31',
      },
      text: {
        primary: '#ffffff',
        secondary: 'rgba(255, 255, 255, 0.7)',
      },
      divider: THEME_COLORS.DIVIDER_DARK,
    } : {
      background: {
        default: THEME_COLORS.BACKGROUND_LIGHT,
        paper: THEME_COLORS.CARD_BACKGROUND_LIGHT,
      },
      text: {
        primary: THEME_COLORS.TEXT_PRIMARY_LIGHT,
        secondary: THEME_COLORS.TEXT_SECONDARY_LIGHT,
      },
      divider: THEME_COLORS.DIVIDER_LIGHT,
    }),
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          '--rn-accent': BRAND_ACCENT,
          '--rn-accent-hover': THEME_COLORS.PRIMARY_HOVER,
          '--rn-bg-dark': THEME_COLORS.BACKGROUND_DARK,
          '--rn-bg-light': THEME_COLORS.BACKGROUND_LIGHT,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            '& fieldset': {
              borderColor: BRAND_ACCENT,
            },
            '&:hover fieldset': {
              borderColor: BRAND_ACCENT,
            },
            '&.Mui-focused fieldset': {
              borderColor: BRAND_ACCENT,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: BRAND_ACCENT,
          },
          '&:hover fieldset': {
            borderColor: BRAND_ACCENT,
          },
          '&.Mui-focused fieldset': {
            borderColor: BRAND_ACCENT,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: BRAND_ACCENT,
            },
            '&:hover fieldset': {
              borderColor: BRAND_ACCENT,
            },
            '&.Mui-focused fieldset': {
              borderColor: BRAND_ACCENT,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.secondary,
          '&.Mui-focused': {
            color: BRAND_ACCENT,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 500,
          letterSpacing: '0.5px',
        },
        containedPrimary: {
          color: '#000000',
          '&:hover': {
            backgroundColor: THEME_COLORS.PRIMARY_HOVER,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: BRAND_ACCENT,
            '& + .MuiSwitch-track': {
              backgroundColor: BRAND_ACCENT,
            },
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: BRAND_ACCENT,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: BRAND_ACCENT,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: BRAND_ACCENT,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: {
          backgroundColor: BRAND_ACCENT,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: BRAND_ACCENT,
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiDrawer: {
      defaultProps: {
        SlideProps: { appear: true },
        transitionDuration: { enter: 350, exit: 250 },
        ModalProps: { keepMounted: true },
      },
      styleOverrides: {
        paperAnchorRight: ({ theme }) => ({
          maxWidth: '100vw',
          boxSizing: 'border-box',
          width: '100vw',
          [theme.breakpoints.up('sm')]: {
            width: 'min(640px, 100vw)',
          },
          [theme.breakpoints.up('md')]: {
            width: 'min(720px, 100vw)',
          },
        }),
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          paddingLeft: 0,
          paddingRight: 0,
          [theme.breakpoints.up('sm')]: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }),
      },
    },
    ...buildDialogThemeOverrides({ accentColor: BRAND_ACCENT }),
  },
});

const getTheme = (mode = 'light', mobileShell = false) => {
  const baseTheme = createTheme(buildBaseThemeOptions(mode));
  if (mobileShell && mode === 'dark') {
    return createTheme(
      baseTheme,
      buildMobileShellDarkTheme(BRAND_ACCENT),
      buildMobileThemeOverrides(BRAND_ACCENT),
      buildDialogThemeOverrides({ accentColor: BRAND_ACCENT }),
      buildMobileDrawerThemeOverrides()
    );
  }
  if (mobileShell) {
    return createTheme(
      baseTheme,
      buildMobileThemeOverrides(BRAND_ACCENT),
      buildDialogThemeOverrides({ accentColor: BRAND_ACCENT }),
      buildMobileDrawerThemeOverrides()
    );
  }
  if (isNativeAppShell()) {
    return createTheme(
      baseTheme,
      buildMobileThemeOverrides(BRAND_ACCENT),
      buildMobileDrawerThemeOverrides()
    );
  }
  return baseTheme;
};

// Detect theme mode from mobile shell, document classes, or localStorage
const getInitialThemeMode = () => detectThemeSync();

const RequireFeaturePermission = ({ permissionCode, children }) => {
  const userPermissionCodes = getUserPermissionCodes();
  const permissionCodes = Array.isArray(permissionCode) ? permissionCode : [permissionCode];
  if (permissionCodes.some((code) => hasFeaturePermission(code, userPermissionCodes))) {
    return children;
  }
  return <Navigate to={getFirstAllowedModulePath(userPermissionCodes)} replace />;
};

const withFeaturePermission = (permissionCode, element) => (
  <RequireFeaturePermission permissionCode={permissionCode}>
    {element}
  </RequireFeaturePermission>
);

function useMobileShellActive() {
  const [active, setActive] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Capacitor iOS/Android (phone + tablet/iPad) always use the mobile app shell.
    if (isNativeAppShell()) return true;
    return window.matchMedia('(max-width:899px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(max-width:899px)');
    const update = () => {
      setActive(isNativeAppShell() || mq.matches);
    };
    update();
    mq.addEventListener('change', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      mq.removeEventListener('change', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return active;
}

function isNexaStayPublicSurface(pathname) {
  return isNexaStaySlugDomainHost() || isNexaStayPublicPathname(pathname);
}

function isPosOpenOrderingPublicSurface(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  return path === '/pos-restaurant/open' || path.startsWith('/pos-restaurant/open/');
}

function isWebCheckInPublicSurface(pathname) {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  return path === '/web-checkin' || path.startsWith('/web-checkin/');
}

/** Guest-facing pages that must scroll on mobile (outside staff app shell). */
function isGuestPublicScrollSurface(pathname) {
  return (
    isNexaStayPublicSurface(pathname) ||
    isPosOpenOrderingPublicSurface(pathname) ||
    isWebCheckInPublicSurface(pathname)
  );
}

/** Staff mobile app shell: lock document scroll. Skip guest/public ordering pages. */
function MobileShellDocumentEffects({ themeMode }) {
  const isMobileShell = useMobileShellActive();
  const location = useLocation();
  const useMobileShellChrome = isMobileShell && !isGuestPublicScrollSurface(location.pathname);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const effectiveTheme = getStoredThemePreference() ?? themeMode;

    document.documentElement.classList.toggle('mobile-shell-theme', useMobileShellChrome);
    document.body.classList.toggle('mobile-shell-theme', useMobileShellChrome);
    document.documentElement.classList.toggle('theme-dark', effectiveTheme === 'dark');
    document.documentElement.classList.toggle('theme-light', effectiveTheme !== 'dark');

    if (useMobileShellChrome) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }, [useMobileShellChrome, themeMode]);

  return null;
}

function StartupRouteGate({ isSlugNexaStayHost }) {
  const isMobileShell = useMobileShellActive();
  const location = useLocation();
  const navigate = useNavigate();
  const djangoContext = window.DJANGO_CONTEXT || {};
  const user = djangoContext.user || {};
  const selectedHotel = djangoContext.selectedHotel || null;
  const authenticated = Boolean(user?.id);
  const hasHotel = Boolean(selectedHotel?.id);
  const isGuestSurface = isSlugNexaStayHost || isGuestPublicScrollSurface(location.pathname);
  const [visible, setVisible] = useState(() => isMobileShell && !isGuestSurface);

  useEffect(() => {
    if (!isMobileShell || isGuestSurface) {
      setVisible(false);
    }
  }, [isGuestSurface, isMobileShell]);

  const handleComplete = useCallback(() => {
    setVisible(false);

    const path = location.pathname;
    const isAuthPath = path.startsWith('/login/') || path.startsWith('/password-reset/');
    const isHotelSelectionPath = path.startsWith('/onboarding/landing/');

    if (!authenticated && !isAuthPath) {
      navigate('/login/', { replace: true });
      return;
    }

    if (authenticated && !hasHotel && !isHotelSelectionPath) {
      navigate('/onboarding/landing/?view=hotels', { replace: true });
      return;
    }

    if (authenticated && hasHotel && (path === '/' || isAuthPath)) {
      navigate('/dashboard/', { replace: true });
    }
  }, [authenticated, hasHotel, location.pathname, navigate]);

  if (!visible) return null;

  return (
    <StartupSequence onComplete={handleComplete} />
  );
}

function App() {
  const [themeMode, setThemeMode] = useState(getInitialThemeMode());
  const isMobileShell = useMobileShellActive();

  useEffect(() => {
    ensureDefaultThemePreference();
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    let releasing = false;

    const releaseHiddenFocus = () => {
      if (releasing) return;
      const active = document.activeElement;
      if (!(active instanceof HTMLElement)) return;

      const hiddenAncestor = active.closest('[aria-hidden="true"]');
      if (!hiddenAncestor) return;

      // Nested MUI Modal/Drawer FocusTrap + this helper can recurse forever if we
      // move focus onto #root while it (or another ancestor) is aria-hidden.
      releasing = true;
      try {
        active.blur();

        const openSurface = document.querySelector(
          '.MuiModal-root:not([aria-hidden="true"]) [tabindex="-1"], .MuiDrawer-root:not([aria-hidden="true"]) .MuiDrawer-paper'
        );
        const fallback =
          (openSurface instanceof HTMLElement && !openSurface.closest('[aria-hidden="true"]')
            ? openSurface
            : null) ||
          document.getElementById('root') ||
          document.body;

        if (!(fallback instanceof HTMLElement)) return;
        if (fallback.closest('[aria-hidden="true"]')) return;

        const hadTabIndex = fallback.hasAttribute('tabindex');
        if (!hadTabIndex) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
        if (!hadTabIndex) {
          window.requestAnimationFrame(() => {
            fallback.removeAttribute('tabindex');
          });
        }
      } finally {
        releasing = false;
      }
    };

    const observer = new MutationObserver(releaseHiddenFocus);
    observer.observe(document.body, {
      attributes: true,
      subtree: true,
      attributeFilter: ['aria-hidden'],
    });

    document.addEventListener('focusin', releaseHiddenFocus, true);

    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', releaseHiddenFocus, true);
    };
  }, []);

  // Watch for theme / viewport changes (keeps MUI theme aligned with chrome)
  useEffect(() => {
    const onThemeChange = () => setThemeMode(detectThemeSync());
    const cleanupObserver = watchThemeChanges(setThemeMode);
    window.addEventListener('roomnexa:theme-change', onThemeChange);
    return () => {
      cleanupObserver();
      window.removeEventListener('roomnexa:theme-change', onThemeChange);
    };
  }, []);

  const theme = useMemo(() => {
    const mode = getStoredThemePreference() ?? themeMode;
    return getTheme(mode, isMobileShell);
  }, [themeMode, isMobileShell]);

  const isSlugNexaStayHost = isNexaStaySlugDomainHost();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <MobileShellDocumentEffects themeMode={themeMode} />
        <ScrollToTop />
        <AppRouteTree isSlugNexaStayHost={isSlugNexaStayHost} />
        <StartupRouteGate isSlugNexaStayHost={isSlugNexaStayHost} />
        <SupportChatFab />
      </Router>
    </ThemeProvider>
  );
}

export default App;
